// Registrierung eines neuen Betriebs.
//
// Legt Mandant und Admin-Zugang in einem Schritt an und meldet direkt an.
// Jeder Tarif startet mit 30 Tagen gratis; das SEPA-Mandat wird erst bis zum
// Ende des Gratismonats gebraucht. Pflicht sind Anschrift und Telefon (sie
// stehen auf jeder Rechnung an den Kunden) und die Zustimmung zu AGB und
// Datenschutz — mit Stand, damit klar ist, wem zugestimmt wurde.
import { RechtsLinks } from '../../rechtliches/komponenten/RechtsRahmen';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiFehler, tokenSetzen } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { Feld, Hinweis, Karte, Knopf } from '../../../gemeinsam/komponenten/Basis';
import { LogoKachel } from '../../firma/komponenten/Logo';
import { ANBIETER_URL, POWERED_BY } from '../../../gemeinsam/lib/marke';
import { istNativ } from '../../../gemeinsam/lib/plattform';

type Tarif = { id: string; label: string; price: number; description: string };
const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

export default function Registrieren() {
  const { aktualisieren } = useAuth();
  const navigate = useNavigate();
  const [parameter] = useSearchParams();

  const [tarife, setTarife] = useState<Tarif[]>([]);
  const [versionen, setVersionen] = useState<{ agb: string; datenschutz: string } | null>(null);
  const [plan, setPlan] = useState(() => {
    const p = parameter.get('tarif');
    return p === 'professional' || p === 'enterprise' ? p : 'starter';
  });
  const [strasse, setStrasse] = useState('');
  const [plz, setPlz] = useState('');
  const [ort, setOrt] = useState('');
  const [land, setLand] = useState('Deutschland');
  const [telefon, setTelefon] = useState('');
  const [zustimmung, setZustimmung] = useState(false);

  useEffect(() => {
    api<{ tarife: Tarif[]; agbVersion: string; datenschutzVersion: string }>('/oeffentlich/tarife?sprache=de')
      .then(r => { setTarife(r.tarife); setVersionen({ agb: r.agbVersion, datenschutz: r.datenschutzVersion }); })
      .catch(() => setFehler('Die Tarife konnten nicht geladen werden. Bitte die Seite neu laden.'));
  }, []);

  const [firma, setFirma] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [benutzername, setBenutzername] = useState('');
  const [passwort, setPasswort] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  // Dieselben Regeln wie im Backend — der Anwender soll sie sehen, bevor er
  // auf Ablehnung läuft.
  const langGenug = passwort.length >= 8;
  const hatGross = /[A-Z]/.test(passwort);
  const hatKlein = /[a-z]/.test(passwort);
  const hatZiffer = /[0-9]/.test(passwort);
  const nameOk = benutzername.trim().length >= 3;
  const gueltig = firma.trim() && email.trim() && name.trim()
    && strasse.trim() && plz.trim() && ort.trim() && land.trim() && telefon.trim()
    && zustimmung && versionen
    && nameOk && langGenug && hatGross && hatKlein && hatZiffer;

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    if (!gueltig || sendet) return;
    setSendet(true);
    setFehler(null);
    try {
      const antwort = await api<{ token: string }>('/auth/register', {
        methode: 'POST',
        daten: {
          tenant: {
            name: firma.trim(), email: email.trim(), plan,
            strasse: strasse.trim(), plz: plz.trim(), ort: ort.trim(), land: land.trim(), telefon: telefon.trim(),
          },
          zustimmung: { agb: true, datenschutz: true, agbVersion: versionen?.agb, datenschutzVersion: versionen?.datenschutz },
          admin: {
            username: benutzername.trim(),
            password: passwort,
            name: name.trim(),
            email: email.trim(),
          },
        },
      });
      // Der Server meldet uns gleich an — kein zweiter Weg über die Anmeldung.
      tokenSetzen(antwort.token);
      await aktualisieren();
      navigate('/', { replace: true });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Registrierung fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  const regel = (erfuellt: boolean, text: string) => (
    <li className={`flex items-center gap-2 ${erfuellt
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-slate-500 dark:text-slate-400'}`}>
      <span aria-hidden>{erfuellt ? '✓' : '○'}</span>{text}
    </li>
  );

  return (
    <div
      style={{
        // In der App ist dies ein Vollbild ohne Kopfleiste: Statusleiste oben
        // und Home-Leiste unten freihalten. Im Browser sind beide Werte 0,
        // es bleibt bei den 3rem von py-12.
        paddingTop: 'calc(3rem + var(--sicher-oben))',
        paddingBottom: 'calc(3rem + var(--sicher-unten))',
      }}
      className="flex min-h-full items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-4 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          {/* In der App gibt es keine Startseite — der Weg zurück führt dort
              auf die Anmeldemaske, und der Text muss das auch sagen. */}
          <Link to={istNativ() ? '/anmelden' : '/'}
                className="text-sm text-slate-500 hover:underline dark:text-slate-400">
            ← {istNativ() ? 'Zurück zur Anmeldung' : 'Zurück zur Startseite'}
          </Link>
          <LogoKachel groesse="gross" klasse="mx-auto mb-4 mt-3 shadow-lg shadow-marke-600/25" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Konto anlegen</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            30 Tage gratis · ohne Bankdaten · monatlich kündbar
          </p>
        </div>

        <Karte>
          <form onSubmit={absenden} className="space-y-4 p-6">
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Tarif — der erste Monat ist gratis</legend>
              <div className="grid gap-2">
                {tarife.map(tf => (
                  <label key={tf.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${plan === tf.id
                    ? 'border-marke-500 bg-marke-50/60 dark:border-marke-600 dark:bg-marke-950/40'
                    : 'border-slate-200 dark:border-slate-700'}`}>
                    <input type="radio" name="tarif" value={tf.id} checked={plan === tf.id} onChange={() => setPlan(tf.id)} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-slate-900 dark:text-slate-100">{tf.label}</span>
                      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{tf.description}</span>
                    </span>
                    <span className="text-right text-sm text-slate-700 dark:text-slate-300">
                      {euro(tf.price)}<span className="block text-xs text-slate-500 dark:text-slate-400">/ Monat netto</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                Wechseln und kündigen jederzeit unter „Tarif". Kündigen Sie im Gratismonat, wird nichts berechnet.
              </p>
            </fieldset>

            <Feld label="Firmenname" value={firma} required autoFocus
                  placeholder="Muster Handel GmbH"
                  onChange={e => setFirma(e.target.value)} />
            <Feld label="E-Mail der Firma" type="email" value={email} required
                  placeholder="info@muster-handel.de"
                  onChange={e => setEmail(e.target.value)} />
            <Feld label="Straße und Hausnummer" value={strasse} required autoComplete="street-address"
                  onChange={e => setStrasse(e.target.value)} />
            <div className="grid grid-cols-[7rem_1fr] gap-3">
              <Feld label="PLZ" value={plz} required autoComplete="postal-code" inputMode="numeric"
                    onChange={e => setPlz(e.target.value)} />
              <Feld label="Ort" value={ort} required autoComplete="address-level2"
                    onChange={e => setOrt(e.target.value)} />
            </div>
            <Feld label="Land" value={land} required autoComplete="country-name"
                  onChange={e => setLand(e.target.value)} />
            <Feld label="Telefon" type="tel" value={telefon} required autoComplete="tel" placeholder="0211 1234567"
                  onChange={e => setTelefon(e.target.value)} />
            <Feld label="Ihr Name" value={name} required
                  placeholder="Petra Muster"
                  onChange={e => setName(e.target.value)} />
            <Feld label="Benutzername für die Anmeldung" value={benutzername} required
                  autoComplete="username" placeholder="pmuster"
                  hinweis={benutzername && !nameOk ? 'Mindestens 3 Zeichen' : undefined}
                  onChange={e => setBenutzername(e.target.value)} />
            <Feld label="Passwort" type="password" value={passwort} required
                  autoComplete="new-password"
                  onChange={e => setPasswort(e.target.value)} />

            <ul className="space-y-1 text-xs">
              {regel(langGenug, 'Mindestens 8 Zeichen')}
              {regel(hatGross && hatKlein, 'Groß- und Kleinbuchstaben')}
              {regel(hatZiffer, 'Mindestens eine Ziffer')}
            </ul>

            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" className="mt-0.5" checked={zustimmung} onChange={e => setZustimmung(e.target.checked)} />
              <span>
                Ich habe die <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-marke-600 underline dark:text-marke-400">AGB</a>{' '}
                und die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-marke-600 underline dark:text-marke-400">Datenschutzerklärung</a>{' '}
                gelesen und stimme zu.
              </span>
            </label>

            <Knopf type="submit" variante="primaer" className="w-full" laedt={sendet} disabled={!gueltig}>
              30 Tage gratis starten
            </Knopf>

            <p className="text-center text-xs text-slate-400 dark:text-slate-500">
              <a href={ANBIETER_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {POWERED_BY}
              </a>
            </p>

            <p className="text-center text-xs text-slate-500 dark:text-slate-400">
              Schon ein Konto? <Link to="/anmelden" className="font-medium text-marke-600 hover:underline dark:text-marke-400">Anmelden</Link>
            </p>
          </form>
        </Karte>
        <RechtsLinks klasse="mt-5" />
      </div>
    </div>
  );
}
